interface UsageData {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  requestCount: number;
  date?: string;
}

interface UsageIndicatorProps {
  usage: UsageData | null;
}

export default function UsageIndicator({ usage }: UsageIndicatorProps) {
  if (!usage) return null;

  const getColorClass = () => {
    if (usage.percentage >= 90) return 'text-red-600 dark:text-red-400';
    if (usage.percentage >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const getStrokeColor = () => {
    // Need to check if dark mode is active
    const isDarkMode = document.documentElement.classList.contains('dark');
    
    if (usage.percentage >= 90) {
      return isDarkMode ? '#f87171' : '#dc2626'; // red-400 (dark) / red-600 (light)
    }
    if (usage.percentage >= 75) {
      return isDarkMode ? '#fbbf24' : '#ca8a04'; // yellow-400 (dark) / yellow-600 (light)
    }
    return isDarkMode ? '#4ade80' : '#16a34a'; // green-400 (dark) / green-600 (light)
  };

  return (
    <div className="relative group">
      {/* Circular Progress */}
      <div className="relative w-10 h-10">
        <svg className="transform -rotate-90 w-10 h-10">
          {/* Background circle */}
          <circle
            cx="20"
            cy="20"
            r="16"
            stroke="currentColor"
            strokeWidth="3"
            fill="none"
            className="text-gray-300 dark:text-gray-700"
          />
          {/* Progress circle */}
          <circle
            cx="20"
            cy="20"
            r="16"
            stroke={getStrokeColor()}
            strokeWidth="3"
            fill="none"
            strokeDasharray={`${2 * Math.PI * 16}`}
            strokeDashoffset={`${2 * Math.PI * 16 * (1 - usage.percentage / 100)}`}
            strokeLinecap="round"
          />
        </svg>
        <span className={`absolute inset-0 flex items-center justify-center text-xs font-bold ${getColorClass()}`}>
          {Math.round(usage.percentage)}%
        </span>
      </div>

      {/* Tooltip - Shows on hover */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-50">
        <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-dark-border rounded-lg px-3 py-2 shadow-xl whitespace-nowrap">
          <div className="text-gray-700 dark:text-dark-text text-xs">
            You've used {Math.round(usage.percentage)}% of your daily usage limit
          </div>
        </div>
      </div>
    </div>
  );
}

